import { useEffect, useMemo, useState } from 'preact/hooks';
import type { Ref } from 'preact';
import { useLocale, useTranslation } from '../i18n';
import { TwoColumnShell, SectionTitleBar, ErrorTile, Skeleton, MobileDrawer } from '../primitives';
import { versionedUrl } from '../utils/versionedUrl';
import { useFetch } from '../hooks/useFetch';
import { api } from '../api/client';
import { useInfiniteWorks } from '../hooks/useInfiniteWorks';
import { WorkRow } from '../components/WorkRow';
import { WorkRowHeader } from '../components/WorkRowHeader';
import { WorkCard } from '../components/WorkCard';
import {
    CatalogFilters,
    DEFAULT_FILTERS,
    buildKoanFilter,
    buildKoanSort,
    parseFiltersFromQuery,
    filtersToQueryString,
} from '../components/CatalogFilters';
import {
    ViewModeToggle,
    readViewMode,
    writeViewMode,
    type ViewMode,
} from '../components/ViewModeToggle';
import { SortDropdown } from '../components/SortDropdown';
import { Layout } from '../components/Layout';

export function CatalogPage() {
    const t = useTranslation();
    const { apiSet } = useLocale();
    // Hydrate filters from the URL so /emporium?type=Preset&host=
    // nexusmods.com is a real bookmarkable surface. With infinite scroll
    // the rolling result set is paginated, so the URL is the only place
    // filter state can live across reload / share / back-navigation.
    const [filters, setFilters] = useState<ReturnType<typeof parseFiltersFromQuery>>(() =>
        parseFiltersFromQuery(typeof window === 'undefined' ? '' : window.location.search),
    );
    const [viewMode, setViewMode] = useState<ViewMode>(() => readViewMode('list'));
    const [filtersOpen, setFiltersOpen] = useState(false);

    useEffect(() => {
        writeViewMode(viewMode);
    }, [viewMode]);

    // Mirror filter state back into the URL via replaceState so each
    // tweak doesn't push a new history entry (the back button would
    // otherwise rewind one chip at a time). pushState semantics would
    // be wrong here.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const qs = filtersToQueryString(filters);
        const target = `${window.location.pathname}${qs}${window.location.hash}`;
        if (target !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
            window.history.replaceState(window.history.state, '', target);
        }
    }, [filters]);

    // Browser back/forward triggers a popstate; the URL changed but our
    // state didn't, so re-parse and apply. Without this, the visible
    // filter chips drift out of sync with what the URL bar says.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onPop = () => setFilters(parseFiltersFromQuery(window.location.search));
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    // Debounce the free-text search term before it reaches the query -
    // chip filters (source/kind/engine/...) stay immediate since those
    // come from discrete clicks, not keystrokes.
    const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(filters.search), 300);
        return () => clearTimeout(handle);
    }, [filters.search]);

    // Push every active axis down to Koan as filter JSON + sort spec so
    // pagination and counts honour them. The tag axes (engine / game /
    // access / locale) are filterable server-side now that the denormalizer
    // projects them into flat top-level Work fields, so there's no
    // client-side residual pass. The infinite-scroll hook detects
    // filter/sort changes via JSON.stringify(query) and resets to page 1.
    const koanFilter = useMemo(
        () => buildKoanFilter({ ...filters, search: debouncedSearch }),
        [filters, debouncedSearch],
    );
    const koanSort = useMemo(() => buildKoanSort(filters), [filters]);
    const summaries = useInfiniteWorks(
        {
            filter: koanFilter ?? undefined,
            sort: koanSort ?? undefined,
        },
        apiSet,
        100,
    );
    // Full server-aggregated facet bundle. One request on mount populates
    // every chip list (source + type/engine/game/access/locale) with stable
    // counts. Without this the sidebar's filter universe grows as
    // infinite-scroll pulls more rows - see /api/catalog/facets.
    const facetsRequest = useFetch(() => api.catalog.facets(), []);
    const facets = facetsRequest.status === 'success' ? facetsRequest.data : undefined;
    // Surface the in-flight state to the sidebar so it renders skeleton
    // placeholders for the core axes (source/kind/engine/game) instead of
    // disappearing entirely until the facet bundle resolves. Works, a
    // paginated top-100 query, routinely win the race against the
    // full-corpus facet aggregation; without the skeleton the user sees a
    // page that has rows but no filters until the slower request lands.
    // Treat both 'idle' (pre-effect) and 'loading' as in-flight so the
    // skeleton paints on the very first frame, not after one flicker.
    const facetsLoading =
        facetsRequest.status === 'idle' || facetsRequest.status === 'loading';

    // All filtering happens server-side now (see buildKoanFilter); the
    // visible set is exactly what the paginated endpoint returned.
    const visible = summaries.items;

    const sourceCount = facets ? facets.source.length : '…';
    const totalCount = summaries.totalCount ?? 0;
    const isFirstLoad = summaries.status === 'loading-first';
    const isLoadingMore = summaries.status === 'loading-more';

    return (
        <Layout>
            <TwoColumnShell
                sidebarWidth={260}
                sticky
                sidebar={
                    <div class="px-6 py-8">
                        <CatalogFilters
                            state={filters}
                            onChange={setFilters}
                            facets={facets}
                            facetsLoading={facetsLoading}
                        />
                    </div>
                }
            >
                <SectionTitleBar
                    eyebrow={
                        summaries.totalCount !== null
                            ? t('catalogV2.eyebrowCount', { n: totalCount, sources: sourceCount })
                            : t('catalog.eyebrow')
                    }
                    title={t('catalogV2.heroTitle')}
                    subtitle={t('catalogV2.heroBody', {
                        sourceList: 'NexusMods, Ko-fi, GitHub, Discord',
                    })}
                    size="lg"
                    actions={
                        <>
                            <button
                                type="button"
                                onClick={() => setFiltersOpen(true)}
                                data-testid="catalog-filters-button"
                                class="inline-flex items-center gap-1.5 rounded-md border border-surface-3 bg-surface-1 px-2.5 py-1.5 text-xs text-fg-secondary transition-colors hover:bg-surface-2 hover:text-fg-primary lg:hidden"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <polygon points="4 4 20 4 14 12 14 19 10 21 10 12 4 4" />
                                </svg>
                                {t('catalogFilters.openFilters')}
                            </button>
                            {summaries.totalCount !== null && (
                                <span class="text-[12px] tracking-[0.04em] text-fg-muted">
                                    <b class="mr-0.5 font-display text-[15px] font-normal italic text-fg-primary">
                                        {visible.length}
                                    </b>{' '}
                                    of{' '}
                                    <b class="ml-0.5 mr-0.5 font-display text-[15px] font-normal italic text-fg-primary">
                                        {summaries.totalCount}
                                    </b>
                                </span>
                            )}
                            {viewMode === 'grid' && (
                                <SortDropdown
                                    value={filters.sort}
                                    onChange={(sort) => setFilters({ ...filters, sort })}
                                />
                            )}
                            <ViewModeToggle value={viewMode} onChange={setViewMode} />
                        </>
                    }
                />

                {isFirstLoad && (
                    <Skeleton.Grid
                        count={viewMode === 'grid' ? 6 : 12}
                        mode={viewMode === 'grid' ? 'card' : 'row'}
                    />
                )}

                {summaries.status === 'error' && summaries.error && (
                    <ErrorTile title={t('catalog.errorTitle')} details={summaries.error.message} />
                )}

                {filters.author && (
                    <ActiveAuthorChip
                        author={filters.author}
                        onClear={() => setFilters({ ...filters, author: '' })}
                    />
                )}

                {!isFirstLoad && summaries.items.length > 0 && visible.length === 0 && (
                    <EmptyState onClear={() => setFilters(DEFAULT_FILTERS)} />
                )}

                {visible.length > 0 &&
                    (viewMode === 'grid' ? (
                        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {visible.map((pkg) => (
                                <WorkCard key={pkg.id} pkg={pkg} />
                            ))}
                        </div>
                    ) : (
                        <div class="overflow-hidden">
                            <WorkRowHeader
                                sort={filters.sort}
                                onChange={(sort) => setFilters({ ...filters, sort })}
                            />
                            {visible.map((pkg) => (
                                <WorkRow key={pkg.id} pkg={pkg} />
                            ))}
                        </div>
                    ))}

                {/* Infinite-scroll sentinel: the observer in
                    <c>useInfiniteWorks</c> watches this element and
                    pulls the next page when it enters the viewport
                    (rootMargin=400px so the fetch fires before the user
                    actually hits the bottom). Hidden when we've reached
                    the last page. */}
                {summaries.hasMore && (
                    <div
                        ref={summaries.sentinelRef as unknown as Ref<HTMLDivElement>}
                        class="flex items-center justify-center py-10"
                    >
                        {isLoadingMore ? (
                            <span class="font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                                {t('catalog.loadingMore')}
                            </span>
                        ) : (
                            <span class="h-px w-12 bg-rule" />
                        )}
                    </div>
                )}
            </TwoColumnShell>

            <MobileDrawer
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title={t('catalogFilters.openFilters')}
                closeLabel={t('catalogFilters.closeFilters')}
            >
                <div class="px-6 py-8">
                    <CatalogFilters
                        state={filters}
                        onChange={setFilters}
                        facets={facets}
                        facetsLoading={facetsLoading}
                    />
                </div>
            </MobileDrawer>
        </Layout>
    );
}

function EmptyState({ onClear }: { onClear: () => void }) {
    const t = useTranslation();
    return (
        <div class="grid place-items-center px-5 py-[clamp(60px,8vw,120px)] text-center">
            <img
                src={versionedUrl('/loporrit-mark-192.png')}
                alt=""
                width="120"
                height="120"
                class="mb-5 w-[120px] opacity-60 [filter:grayscale(0.4)]"
            />
            <h3 class="font-display text-3xl font-normal italic text-fg-primary">
                {t('catalog.emptyTitle')}
            </h3>
            <p class="mt-2 max-w-[42ch] text-sm text-fg-muted">{t('catalog.emptyBody')}</p>
            <button
                type="button"
                onClick={onClear}
                class="mt-5 rounded-full border border-accent bg-accent-glow px-4 py-1.5 font-mono text-[11px] uppercase tracking-wider text-accent transition hover:bg-accent hover:text-surface-base"
            >
                {t('catalog.emptyClearCta')}
            </button>
        </div>
    );
}

/**
 * Active-author filter indicator. Shown above the result grid when the
 * catalog is filtered to a single creator via the
 * <c>?author=</c> URL param (driven by the package detail page's
 * byline link). The chip is the only place users see the author
 * filter, because authors are free-text and don't have a sidebar
 * facet list - so it doubles as the off-switch.
 */
function ActiveAuthorChip({
    author,
    onClear,
}: {
    author: string;
    onClear: () => void;
}) {
    const t = useTranslation();
    return (
        <div class="mb-4 inline-flex items-center gap-2 rounded-full border border-accent bg-accent-glow px-3 py-1 text-[12.5px] text-accent">
            <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-accent/80">
                {t('catalog.authorChipLabel')}
            </span>
            <span class="italic">{author}</span>
            <button
                type="button"
                onClick={onClear}
                aria-label={t('catalog.authorChipClearAria', { author })}
                title={t('catalog.authorChipClearTitle')}
                class="grid h-4 w-4 place-items-center rounded-full text-accent transition-colors hover:bg-accent hover:text-surface-base"
            >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
                    <line x1="5" y1="5" x2="19" y2="19" />
                    <line x1="19" y1="5" x2="5" y2="19" />
                </svg>
            </button>
        </div>
    );
}
